import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Logger } from '@saviynt/common';
import {
  ButtonIcon,
  Icon,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import { getPredefinedComments } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_APPLICATION_OWNER,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  CAMPAIGNTYPE_USERMANAGER,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import {
  enablePredefinedCommentReassign,
  enableReassignPopover,
} from '../CertsActionButton/utils';
import { LINE_ITEM_TYPES } from '../CertsActionButtonsGroup/constants';
import {
  ACTION_BUTTON_KINDS,
  PREDEFINED_COMMENTS_FOR_ACTION,
} from '../constant';

import CertsReassignContent from './CertsReassignContent';
import CertsReassignFooter from './CertsReassignFooter';
import CertsReassignWarnModal from './CertsReassignWarnModal';
import { invertedMessages } from './messages';
import { getReassignSubTitle } from './utils';

import './CertsReassignModal.css';

function CertsReassignModal({
  isReassignModalOpen,
  setIsReassignModalOpen,
  setIsActionDrawerOutsideClick,
  reassignModalDetails,
  nodeData,
  isCommentRequired,
  onSubmit,
}) {
  const dispatch = useDispatch();

  const {
    core: { predefinedComments, campaignType },
    global: { backNavigation, certKey, mediaQueryValue },
    lineActions: {
      isActionLoading,
      isActionSuccess,
      isWarnModalReassignShown,
      reassignConfigMap,
    },
  } = useSelector((state) => state?.certifications?.details);

  const l = useInvertedLocalize(invertedMessages);

  const [isOpen, setIsOpen] = useState(false);
  const [isIncludeAssociateChecked, setIsIncludedAssociateChecked] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isReassignWarnModalOpen, setIsReassignWarnModalOpen] = useState(false);
  const [isSelectUserCritical, setIsSelectUserCritical] = useState(false);

  const { isSecondaryCertifierLoggedIn } = reassignConfigMap;

  const isReassignUser =
    reassignModalDetails?.actionKind ===
    ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME;
  const isReassignUserEnabled = enableReassignPopover(reassignConfigMap);
  const isPredefinedCommentsWithReassign =
    enablePredefinedCommentReassign(reassignConfigMap);

  //  AO for secondary certifier
  const isCertifierNeededForAOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_APPLICATION_OWNER &&
    (isSecondaryCertifierLoggedIn || !backNavigation.consulteeLoggedIn);

  // UM Campaign
  const isCertifierNeededForUMCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_USERMANAGER &&
    ((isReassignUser && isReassignUserEnabled) || !isReassignUser) &&
    !backNavigation.consulteeLoggedIn;

  // // SA certifier required
  const isCertifierNeededForSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT &&
    enableReassignPopover(reassignConfigMap) &&
    (isSecondaryCertifierLoggedIn || !backNavigation.consulteeLoggedIn);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isSACampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;
  const dropdownClassName = classNames('CertsReassignModal-dropdown');

  const [commentsDetails, setCommentDetails] = useState({
    commentDescription: '',
    predefComments: [],
    isCommentApplied: false,
    showAlert: false,
  });
  const commentTrimCondition = commentsDetails?.commentDescription
    ?.replace(/[<>]/g, '')
    ?.trim();

  const isShowUpdateAccountOwnerEnabled =
    !backNavigation.consulteeLoggedIn &&
    !reassignConfigMap.enableAccessVerificationOnly &&
    reassignConfigMap.enableReassignUser.includes('1') &&
    reassignConfigMap.showUpdateAccountOwner === '1' &&
    reassignConfigMap.showReassignPopup === '1' &&
    reassignModalDetails?.lineItemKind === LINE_ITEM_TYPES.ACCOUNT &&
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const isCertifierNeededforEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER &&
    enableReassignPopover(reassignConfigMap) &&
    (isSecondaryCertifierLoggedIn || !backNavigation.consulteeLoggedIn);

  const showCertifier =
    isCertifierNeededForAOCampaign ||
    isCertifierNeededForUMCampaign ||
    isCertifierNeededForSACampaign ||
    isCertifierNeededforEOCampaign;

  const validateReassignComments = () => isCommentRequired;

  useEffect(() => {
    setIsOpen(isReassignModalOpen);
  }, [isReassignModalOpen]);

  useEffect(() => {
    if (isOpen) setIsActionDrawerOutsideClick(false);
  }, [isOpen]);

  useEffect(() => {
    if (isActionSuccess) {
      setIsReassignModalOpen(false);
      if (isReassignWarnModalOpen) setIsReassignWarnModalOpen(false);
    }
  }, [isActionSuccess]);

  // predefined comments
  const actionType = useMemo(() => {
    const validateComments = validateReassignComments();

    if (!validateComments) return null;

    let type = PREDEFINED_COMMENTS_FOR_ACTION.REASSIGN;

    if (isReassignUser) {
      type =
        isReassignUserEnabled &&
        !backNavigation.consulteeLoggedIn &&
        isPredefinedCommentsWithReassign
          ? PREDEFINED_COMMENTS_FOR_ACTION.DOES_NOT_WORK_FOR_ME
          : PREDEFINED_COMMENTS_FOR_ACTION.DOES_NOT_WORK_FORME_WITHOUT_REASSIGNMENT;
    }

    if (
      isSACampaign &&
      reassignModalDetails?.actionKind ===
        ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME
    ) {
      type = PREDEFINED_COMMENTS_FOR_ACTION.DOES_NOT_BELONG_TO_ME;
    }

    if (
      isEOCampaign &&
      reassignModalDetails?.actionKind ===
        ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME
    ) {
      type = PREDEFINED_COMMENTS_FOR_ACTION.DOES_NOT_BELONG_TO_ME;
    }

    return type;
  }, [
    isCommentRequired,
    isReassignUser,
    isReassignUserEnabled,
    backNavigation.consulteeLoggedIn,
    isPredefinedCommentsWithReassign,
  ]);

  useEffect(() => {
    if (!actionType) return;

    if (!(actionType in predefinedComments)) {
      dispatch(getPredefinedComments(actionType)).catch((error) =>
        Logger.log(error)
      );
    }
  }, [actionType]);

  useEffect(() => {
    if (!actionType) return;

    if (actionType in predefinedComments) {
      setCommentDetails((prevState) => ({
        ...prevState,
        predefComments: predefinedComments[actionType] || [],
      }));
    }
  }, [actionType, predefinedComments]);

  const handleCommentDescription = (text) => {
    setCommentDetails({
      ...commentsDetails,
      commentDescription: text,
      showAlert: text.length === 0 ? commentsDetails.showAlert : false,
    });
  };

  /** to enable handle submit button */
  const isBtnEnabled = () => {
    const validateComments = validateReassignComments();

    if (isActionLoading) return false;

    // consultee login enable only comments
    if (
      isReassignUser &&
      backNavigation?.consulteeLoggedIn &&
      commentTrimCondition?.length >= 3
    )
      return true;

    // is button enabled
    if (
      isReassignUser &&
      !isReassignUserEnabled &&
      validateComments &&
      commentTrimCondition?.length >= 3
    )
      return true;

    if (!validateComments) {
      if (selectedUser) return true;
    }

    // selected user and comments validation
    if (validateComments && commentTrimCondition?.length >= 3 && selectedUser)
      return true;

    // comment validation
    if (validateComments && commentTrimCondition?.length >= 3) return true;

    // only with comments
    if (
      !showCertifier &&
      validateComments &&
      commentTrimCondition?.length >= 3
    ) {
      return true;
    }

    return false;
  };

  const handleSubmit = (e, isFromWarnModal = false) => {
    let proceedSubmit = true;
    const validateComments = validateReassignComments();

    if (validateComments && commentTrimCondition?.length < 3) {
      proceedSubmit = false;
      setCommentDetails({
        ...commentsDetails,
        showAlert: true,
      });
    }

    // Certifier required condition check for UM  , isCertifierNeededForAOCampaign  for AO campaign
    if (showCertifier && !selectedUser) {
      setIsSelectUserCritical(true);
      proceedSubmit = false;
    }

    if (!proceedSubmit) return;

    if (isBtnEnabled()) {
      const modalInfo = {
        isIncludeAssociateChecked,
        selectedUser,
        certKey,
      };

      // is button enabled
      if (isReassignUser && !isReassignUserEnabled && validateComments) {
        if (commentTrimCondition?.length >= 3) {
          // when comments only required for User(DNWFM)
          if (validateComments && commentsDetails?.commentDescription) {
            modalInfo.commentValue = commentsDetails.commentDescription;
          }

          onSubmit(modalInfo);

          return;
        }
      }

      // hanlde comments validation
      if (validateComments && commentTrimCondition?.length < 3) {
        setCommentDetails({
          ...commentsDetails,
          showAlert: true,
        });

        return;
      }

      if (validateComments && commentsDetails?.commentDescription)
        modalInfo.commentValue = commentsDetails.commentDescription;

      if (isWarnModalReassignShown || isFromWarnModal) {
        // call api directly from here
        onSubmit(modalInfo);
      } else {
        setIsReassignWarnModalOpen(true);
      }
    }
  };

  const getReassignModal = () => {
    const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
      (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
    );
    const isMobileWidth = mediaQueryValue >= mobileXlargeQuery.value;

    const reassignContent = (
      <CertsReassignContent
        handleCommentDescription={handleCommentDescription}
        nodeData={nodeData}
        setSelectedUser={setSelectedUser}
        selectedUser={selectedUser}
        reassignModalDetails={reassignModalDetails}
        dropdownClassName={dropdownClassName}
        isCommentRequired={validateReassignComments()}
        commentsDetails={commentsDetails}
        setIsIncludedAssociateChecked={setIsIncludedAssociateChecked}
        isIncludeAssociateChecked={isIncludeAssociateChecked}
        isSelectUserCritical={isSelectUserCritical}
        setIsSelectUserCritical={setIsSelectUserCritical}
        isMobileWidth={isMobileWidth}
        isShowUpdateAccountOwnerEnabled={isShowUpdateAccountOwnerEnabled}
        showCertifier={showCertifier}
      />
    );

    if (isMobileWidth) {
      return (
        <MobileDrawer
          className='CertsReassignModal-mobileDrawer'
          isOpen={isOpen}
          setIsOpen={() => setIsReassignModalOpen(false)}
          headerContent={
            <header className='CertsReassignModal-mobileDrawerHeader'>
              <Icon kind='Reassign' color='neutral-1000' size='medium' />
              <div className='CertsReassignModal-mobileDrawerTitleContainer'>
                <Typography
                  kind='h1'
                  className='CertsReassign-mobileDrawerTitle'>
                  {l('Reassign')}
                </Typography>
                <Typography
                  kind='body2'
                  className='CertsReassignModal-mobileDrawerSubTitle'>
                  {getReassignSubTitle(isEOCampaign, nodeData, l)}
                </Typography>
              </div>
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Close')}
                icon={
                  <Icon kind='close' color='neutral-100' size='smallMedium' />
                }
                onClick={() => setIsReassignModalOpen(false)}
              />
            </header>
          }
          content={reassignContent}
          footerActionBar={
            <CertsReassignFooter
              isActionLoading={isActionLoading}
              setIsReassignModalOpen={setIsReassignModalOpen}
              isBtnEnabled={isBtnEnabled}
              handleSubmit={handleSubmit}
              cancelText={l('Cancel')}
              submitText={l('Submit')}
            />
          }
        />
      );
    }

    return (
      <ModalIsland
        isOpen={isOpen}
        isOverflowVisible={!validateReassignComments()}
        onClose={() => {
          setIsOpen(false);
          setIsReassignModalOpen(false);
        }}
        className='CertsReassignModal'
        headerIcon={<Icon kind='Reassign' color='neutral-1000' size='large' />}
        title={l('Reassign')}
        subtitle={getReassignSubTitle(isEOCampaign, nodeData, l)}
        contentBody={reassignContent}
        FooterActionBarComp={
          <CertsReassignFooter
            isActionLoading={isActionLoading}
            setIsReassignModalOpen={setIsReassignModalOpen}
            isBtnEnabled={isBtnEnabled}
            handleSubmit={handleSubmit}
            cancelText={l('Cancel')}
            submitText={l('Submit')}
          />
        }
      />
    );
  };

  return (
    <section>
      {!isReassignWarnModalOpen && getReassignModal()}
      {isReassignWarnModalOpen && (
        <CertsReassignWarnModal
          isReassignWarnModalOpen={isReassignWarnModalOpen}
          isActionLoading={isActionLoading}
          handleClose={() => {
            setIsReassignWarnModalOpen(false);
            setIsReassignModalOpen(false);
          }}
          handleWarnModal={handleSubmit}
        />
      )}
    </section>
  );
}

CertsReassignModal.propTypes = {
  isReassignModalOpen: PropTypes.bool.isRequired,
  setIsReassignModalOpen: PropTypes.func.isRequired,
  reassignModalDetails: PropTypes.shape({
    isIncludeAssociatedEnt: PropTypes.bool,
    isNoteRequired: PropTypes.bool,
    noteText: PropTypes.string,
    isIncludeAsscLabel: PropTypes.string,
    actionKind: PropTypes.string,
    lineItemKind: PropTypes.string,
  }),
  setIsActionDrawerOutsideClick: PropTypes.func.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  isCommentRequired: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

CertsReassignModal.defaultProps = { reassignModalDetails: {} };

export default CertsReassignModal;
